
import React from "react";
import { useTranslation } from 'react-i18next';
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { AtSign, Phone, MapPin } from "lucide-react";
import { LinkedinIcon, XiaohongshuIcon, BilibiliIcon, YoutubeIcon, InstagramIcon } from "@/components/SocialMediaIcons";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { FAQStructuredData } from "@/components/seo/FAQStructuredData";

const AboutUs = () => {
  const { t } = useTranslation('about');

  // FAQ items for structured data
  const faqItems = [
    {
      question: t('faqItems.howItWorks.question'),
      answer: t('faqItems.howItWorks.answer')
    },
    {
      question: t('faqItems.practitionersVerified.question'),
      answer: t('faqItems.practitionersVerified.answer')
    },
    {
      question: t('faqItems.serviceScope.question'),
      answer: t('faqItems.serviceScope.answer')
    },
    {
      question: t('faqItems.insuranceReimbursement.question'),
      answer: t('faqItems.insuranceReimbursement.answer')
    },
    {
      question: t('faqItems.bookAppointment.question'),
      answer: t('faqItems.bookAppointment.answer')
    },
    {
      question: t('faqItems.appointmentChanges.question'),
      answer: t('faqItems.appointmentChanges.answer')
    },
    {
      question: t('faqItems.serviceChanges.question'),
      answer: t('faqItems.serviceChanges.answer')
    },
    {
      question: t('faqItems.cancelAppointment.question'),
      answer: t('faqItems.cancelAppointment.answer')
    },
    {
      question: t('faqItems.refundProcess.question'),
      answer: t('faqItems.refundProcess.answer')
    },
    {
      question: t('faqItems.contactSupport.question'),
      answer: t('faqItems.contactSupport.answer')
    },
    {
      question: t('faqItems.paymentSecurity.question'),
      answer: t('faqItems.paymentSecurity.answer')
    },
    {
      question: t('faqItems.disputeResolution.question'),
      answer: t('faqItems.disputeResolution.answer')
    },
    {
      question: t('faqItems.healingStages.question'),
      answer: t('faqItems.healingStages.answer')
    },
    {
      question: t('faqItems.pricing.question'),
      answer: t('faqItems.pricing.answer')
    },
    {
      question: t('faqItems.sessionFormats.question'),
      answer: t('faqItems.sessionFormats.answer')
    },
    {
      question: t('faqItems.choosePractitioner.question'),
      answer: t('faqItems.choosePractitioner.answer')
    },
    {
      question: t('faqItems.privacy.question'),
      answer: t('faqItems.privacy.answer')
    },
    {
      question: t('faqItems.techRequirements.question'),
      answer: t('faqItems.techRequirements.answer')
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <FAQStructuredData faqItems={faqItems} />
      <Navbar />
      <main className="flex-grow bg-amber-50/50 pt-16 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Company Vision Section */}
          <section className="mb-16">
            <h1 className="text-3xl md:text-4xl font-serif font-medium text-earth-900 mb-6 text-center">
              {t('ourVision')}
            </h1>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-amber-100">
              <p className="text-lg text-earth-700 leading-relaxed mb-6">
                {t('visionDescription1')}
              </p>
              <p className="text-lg text-earth-700 leading-relaxed">
                {t('visionDescription2')}
              </p>
            </div>
          </section>

          {/* Q&A Section with Accordion */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-serif font-medium text-earth-900 mb-6 text-center">
              {t('faq')}
            </h2>
            <div className="bg-white rounded-lg shadow-sm border border-amber-100">
              <Accordion type="single" collapsible className="divide-y divide-amber-100">
                <AccordionItem value="item-1" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.howItWorks.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.howItWorks.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-2" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.practitionersVerified.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.practitionersVerified.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-3" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.serviceScope.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.serviceScope.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-4" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.insuranceReimbursement.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.insuranceReimbursement.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-5" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.bookAppointment.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.bookAppointment.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-6" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.appointmentChanges.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.appointmentChanges.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-7" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.serviceChanges.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.serviceChanges.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-8" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.cancelAppointment.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.cancelAppointment.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-9" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.refundProcess.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.refundProcess.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-10" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.contactSupport.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.contactSupport.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-11" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.paymentSecurity.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.paymentSecurity.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-12" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.disputeResolution.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.disputeResolution.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-13" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.healingStages.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.healingStages.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-14" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.pricing.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.pricing.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-15" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.sessionFormats.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.sessionFormats.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-16" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.choosePractitioner.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.choosePractitioner.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-17" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.privacy.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.privacy.answer')}
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-18" className="border-none">
                  <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-amber-700">
                    {t('faqItems.techRequirements.question')}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-earth-700">
                    {t('faqItems.techRequirements.answer')}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </section>

          {/* Contact Us Section */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-serif font-medium text-earth-900 mb-6 text-center">
              {t('contactUs')}
            </h2>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-amber-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <MapPin className="h-5 w-5 text-amber-600 mr-3 mt-1" />
                    <div>
                      <p className="font-medium">{t('address')}</p>
                      <p className="text-earth-700">425 Washington Blvd, Jersey City, NJ 07310</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Phone className="h-5 w-5 text-amber-600 mr-3 mt-1" />
                    <div>
                      <p className="font-medium">{t('phone')}</p>
                      <p className="text-earth-700">1-909-905-7788</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <AtSign className="h-5 w-5 text-amber-600 mr-3 mt-1" />
                    <div>
                      <p className="font-medium">{t('email')}</p>
                      <p className="text-earth-700">info@resonow.org</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <p className="font-medium mb-2">{t('connectWithUs')}</p>
                  <div className="flex space-x-6">
                    <a 
                      href="http://linkedin.com/company/resonow" 
                      className="hover:opacity-80 transition-opacity"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LinkedinIcon size={32} />
                    </a>
                    <a 
                      href="https://www.xiaohongshu.com/user/profile/63e3d3dd0000000027028c96?xsec_token=YB69VkF1ScEtyl3APnzi4mfU_wUdQD2J6tJCMmyCbIroI=&xsec_source=app_share&xhsshare=CopyLink&appuid=63e3d3dd0000000027028c96&apptime=1748829819&share_id=4b83f0e9d7254de5930d6cd2ce16c2d6"
                      className="hover:opacity-80 transition-opacity"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <XiaohongshuIcon size={32} />
                    </a>
                    <a href="#" className="hover:opacity-80 transition-opacity">
                      <BilibiliIcon size={32} />
                    </a>
                    <a 
                      href="https://www.youtube.com/channel/UC33iC81J5UUfMiaNSiReu8w" 
                      className="hover:opacity-80 transition-opacity"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <YoutubeIcon size={32} />
                    </a>
                    <a 
                      href="https://www.instagram.com/resonow.official?igsh=aTFlOW90cjZxaHV3" 
                      className="hover:opacity-80 transition-opacity"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <InstagramIcon size={32} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Join Our Team Section */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-serif font-medium text-earth-900 mb-6 text-center">
              {t('joinTeam.title')}
            </h2>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-amber-100">
              <p className="text-lg text-earth-700 leading-relaxed mb-6 text-center">
                {t('joinTeam.description')}
              </p>
              <div className="text-center">
                <Button asChild size="lg" className="bg-amber-600 hover:bg-amber-700">
                  <Link to="/careers">{t('joinTeam.viewPositions')}</Link>
                </Button>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AboutUs;
